import { ampli } from '@/ampli';
import type { ConnectedWallet } from '@privy-io/react-auth';
import { useContext } from 'react';

import type { CrossChainStep } from '@/lib/api/0x-cross-chain/types';

import { LiquiditySources } from '@/components/LiquiditySources';
import { isSankeyRouteAvailable } from '@/components/SankeyDiagram/utils';
import { TextSkeleton } from '@/components/Skeleton';
import { Switch } from '@/components/Switch';
import { getBridgeDisplayName } from '@/components/TransactionModule/CrossChainMode/bridgeProviders';
import { ReceivingWalletLineCrossChainMode } from '@/components/TransactionModule/CrossChainMode/ReceivingWalletLine';

import { NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID } from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import { useIsSmartWallet } from '@/hooks/useIsSmartWallet';
import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';
import { useResolvedSlippage } from '@/hooks/useResolvedSlippage';

import {
  approvalSettingsSelector,
  approveWithoutBalanceDependencies,
  maxApprovalToggleVisible,
  useApprovalSettingsStore,
} from '@/store/approvalSettings';
import {
  DEFAULT_SLIPPAGE_PERCENT,
  slippageSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { fontStyles } from '@/styles/typography';

import { BridgeAvatar } from '@/ui/Avatars/BridgeAvatar';
import { ArrowDownIcon } from '@/ui/Icons/ArrowDownIcon';
import { ExpandIcon } from '@/ui/Icons/ExpandIcon';
import { InfoCircleSolidSmallIcon } from '@/ui/Icons/InfoCircleSolidSmallIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipBottomSheet,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { hasInTokenGasFee, Price, Quote } from '@/utils/0x.types';
import { TokenResult } from '@/utils/0x/token-registry.types';
import { LiquiditySource, LiquiditySourceMultiHop } from '@/utils/liquidity-sources';
import { isTokenAddressNativeAsset } from '@/utils/multichain';

import { SankeyContext } from '../../../SankeyDiagram/SankeyProvider';
import { getFeeOnTransferFromPair, hasFeeOnTransfer } from '../utils';
import { MaxSlippageLineItemWithPopover } from './MaxSlippage';
import { PriceRecapMarketMode, PriceRecapCrossChainMode } from './PriceRecap';
import { ReceivingWalletLine } from './Recipient';
import { RouteDetailsTooltip } from './RouteDetailsTooltip';

type ExpandedContentMarketModeProps = {
  isLoading: boolean;
  isWrap: boolean;
  isCurrentSwapGasless: boolean;
  sellToken: TokenResult;
  buyToken: TokenResult;
  chainId: number;
  currentPrice: Price | Quote | undefined;
  liquiditySources: LiquiditySource[] | LiquiditySourceMultiHop[] | undefined;
  selectedLiquiditySourceCount: number;
  solanaMaxSlippageBps: number | undefined;
  walletAddress: string | undefined;
  connectedWallet: MatchaConnectedWallet<ConnectedWallet> | undefined;
  nativeTokenUsd: number;
  tokenUsdPrices: { buyTokenUsd: number; sellTokenUsd: number };
};

export const ExpandedContentMarketMode = ({
  isLoading,
  isWrap,
  isCurrentSwapGasless,
  sellToken,
  buyToken,
  chainId,
  currentPrice,
  liquiditySources,
  selectedLiquiditySourceCount,
  solanaMaxSlippageBps,
  walletAddress,
  connectedWallet,
  nativeTokenUsd,
  tokenUsdPrices,
}: ExpandedContentMarketModeProps) => {
  const { slippage } = useMarketTradeSettingsStore(slippageSelector);
  const { setSankeyModalOpen } = useContext(SankeyContext);
  const isSankeyAvailable = isSankeyRouteAvailable(currentPrice, chainId);
  const handleOpenSankeyModal = () => {
    if (!isSankeyAvailable) return;
    ampli.sankeyChartExpanded({ ui_location: 'swap_details_accordion' });
    setSankeyModalOpen(true);
  };
  const { allowApproveWithoutBalance, setAllowApproveWithoutBalance, maxApproval, setMaxApproval } =
    useApprovalSettingsStore(approvalSettingsSelector);
  const isSmartWallet = useIsSmartWallet({ chainId });
  const isSolana = chainId === CHAIN_IDS.SOLANA;

  // Result-or-estimate (like Solana's `solanaMaxSlippageBps ?? slippage`): prefer the
  // value the current price/quote was actually sent with (echoed onto the response at
  // fetch time; a server-resolved Model B value would win over the echo), falling back
  // to the live resolver estimate while no price has landed yet.
  const {
    bps: autoResolvedBps,
    isReady: isAutoSlippageReady,
    isAuto: isAutoSlippageActive,
  } = useResolvedSlippage({ chainId, sellToken, buyToken });
  const quoteAppliedSlippageBps =
    currentPrice && 'appliedSlippageBps' in currentPrice
      ? currentPrice.appliedSlippageBps
      : undefined;
  const autoSlippageBps =
    isAutoSlippageActive && !isSolana ? (quoteAppliedSlippageBps ?? autoResolvedBps) : undefined;

  const slippagePercentageFormatted =
    typeof solanaMaxSlippageBps === 'number'
      ? solanaMaxSlippageBps / 100
      : autoSlippageBps !== undefined
        ? autoSlippageBps / 100
        : typeof slippage === 'number'
          ? slippage * 100
          : DEFAULT_SLIPPAGE_PERCENT * 100;

  const feeOnTransferValues = getFeeOnTransferFromPair({
    tokenMetadata: currentPrice?.tokenMetadata,
    buyTokenSymbol: buyToken.symbol,
    sellTokenSymbol: sellToken.symbol,
  });

  const approveWithoutBalanceVisible =
    approveWithoutBalanceDependencies(isSolana, isSmartWallet) &&
    !!currentPrice?.issues?.allowance?.spender;

  const isNativeSellToken = !!isTokenAddressNativeAsset(sellToken.address, chainId);

  const showMaxApprovalToggle = maxApprovalToggleVisible({
    isSolana,
    isSmartWallet,
    isGasless: isCurrentSwapGasless,
    isNativeSellToken,
  });

  const handleMaxApprovalChange = (checked: boolean) => {
    setMaxApproval(checked);
    ampli.maxApprovalToggled({ enabled: checked });
  };

  return (
    <>
      <div className="flex items-center justify-between h-8 w-full">
        <div
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'flex items-center text-token-gray-800',
          })}
        >
          Network cost
          <MatchaTooltip delayDuration={0}>
            <MatchaTooltipTrigger>
              <span>
                <InfoCircleSolidSmallIcon className="text-token-gray-700 ml-1 flex justify-center cursor-default hover:cursor-default" />
              </span>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>
              {isCurrentSwapGasless
                ? 'Matcha handles the gas, so you can trade without holding native tokens. Network costs are embedded into the trade.'
                : 'Fee required in order to successfully conduct a transaction on the blockchain.'}
            </MatchaTooltipContent>
            <MatchaTooltipPopover className="max-w-[282px]" />
            <MatchaTooltipBottomSheet header="Network cost" />
          </MatchaTooltip>
        </div>
        {isLoading || !currentPrice ? (
          <TextSkeleton width={150} height={14} />
        ) : (
          <div className="flex items-center">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              <PriceRecapMarketMode
                isCurrentSwapGasless={isCurrentSwapGasless}
                sellToken={sellToken}
                buyToken={buyToken}
                fees={hasInTokenGasFee(currentPrice) ? currentPrice.fees : undefined}
                totalNetworkFee={
                  'totalNetworkFee' in currentPrice ? currentPrice.totalNetworkFee : undefined
                }
                nativeTokenUsd={nativeTokenUsd}
                tokenUsdPrices={tokenUsdPrices}
              />
            </span>
          </div>
        )}
      </div>

      {hasFeeOnTransfer(currentPrice?.tokenMetadata) &&
        Object.values(feeOnTransferValues).map((value) => {
          if (!value) return null;
          const { feeOnTransferToken, feeOnTransferValue } = value;
          const feeOnTransferPercentage = feeOnTransferValue / 100;

          return (
            <div className="flex items-center justify-between h-8 w-full" key={feeOnTransferToken}>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center text-token-gray-800',
                })}
              >
                {feeOnTransferToken} buy/sell tax
                <MatchaTooltip delayDuration={0}>
                  <MatchaTooltipTrigger>
                    <span>
                      <InfoCircleSolidSmallIcon className="text-token-gray-700 ml-1 flex justify-center cursor-default hover:cursor-default" />
                    </span>
                  </MatchaTooltipTrigger>
                  <MatchaTooltipContent>
                    Some tokens have a buy/sell tax and will take a percentage of a token amount
                    when any transfer of the token is performed. Matcha does not receive any of
                    these fees.
                  </MatchaTooltipContent>
                  <MatchaTooltipPopover className="max-w-[282px]" />
                  <MatchaTooltipBottomSheet header={`${feeOnTransferToken} buy/sell tax`} />
                </MatchaTooltip>
              </div>

              {isLoading || !currentPrice ? (
                <TextSkeleton width={150} height={14} />
              ) : (
                <div className="flex items-center">
                  <span
                    className={fontStyles({
                      variant: 'BODY_MD',
                      class: 'text-token-gray-800 flex items-center',
                    })}
                  >
                    <span className="text-token-gray-1600">{feeOnTransferPercentage}%</span>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      {!isWrap && (
        <MaxSlippageLineItemWithPopover
          isLoading={isLoading || (isAutoSlippageActive && !isAutoSlippageReady)}
          slippagePercentageFormatted={slippagePercentageFormatted}
          isGasless={isCurrentSwapGasless}
          isAutoSlippage={isAutoSlippageActive}
        />
      )}
      {showMaxApprovalToggle && (
        <div className="flex items-center justify-between h-8 w-full">
          <div
            className={fontStyles({
              variant: 'BODY_MD',
              class: 'flex items-center text-token-gray-800',
            })}
          >
            Max. approval
            <MatchaTooltip delayDuration={0}>
              <MatchaTooltipTrigger>
                <span>
                  <InfoCircleSolidSmallIcon className="text-token-gray-700 ml-1 flex justify-center cursor-default hover:cursor-default" />
                </span>
              </MatchaTooltipTrigger>
              <MatchaTooltipContent>
                Grants Matcha unlimited spending of this token, so future swaps skip the approval
                step. When turned off, every swap needs a fresh approval for the exact amount
                you&apos;re trading.
              </MatchaTooltipContent>
              <MatchaTooltipPopover className="max-w-[290px]" />
              <MatchaTooltipBottomSheet header="Max. approval" />
            </MatchaTooltip>
          </div>
          <Switch checked={maxApproval} onCheckedChange={handleMaxApprovalChange} />
        </div>
      )}
      {approveWithoutBalanceVisible && (
        <div className="flex items-center justify-between h-8 w-full">
          <div
            className={fontStyles({
              variant: 'BODY_MD',
              class: 'flex items-center text-token-gray-800',
            })}
          >
            Approve without balance
            <MatchaTooltip delayDuration={0}>
              <MatchaTooltipTrigger>
                <span>
                  <InfoCircleSolidSmallIcon className="text-token-gray-700 ml-1 flex justify-center cursor-default hover:cursor-default" />
                </span>
              </MatchaTooltipTrigger>
              <MatchaTooltipContent>
                When enabled, you can approve tokens for trading without having sufficient balance.
                This is useful if you want to pre-approve tokens before funding your wallet.
              </MatchaTooltipContent>
              <MatchaTooltipPopover className="max-w-[290px]" />
              <MatchaTooltipBottomSheet header="Approve without balance" />
            </MatchaTooltip>
          </div>
          <Switch
            checked={allowApproveWithoutBalance}
            onCheckedChange={setAllowApproveWithoutBalance}
          />
        </div>
      )}
      <div className="flex items-center justify-between h-8 w-full">
        {isLoading || !currentPrice || (!liquiditySources && !isWrap) ? (
          <>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Route
            </div>
            <TextSkeleton width={110} height={14} />
          </>
        ) : liquiditySources && isSankeyAvailable ? (
          <div
            role="button"
            className="flex items-center justify-between w-auto grow-1 -mx-3 px-3 py-1.5 rounded-sm transition-colors duration-[150ms] ease-in-out hover:cursor-pointer hover:bg-token-alphalight-200"
            onClick={handleOpenSankeyModal}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpenSankeyModal();
              }
            }}
            tabIndex={0}
          >
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Route
            </div>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              <LiquiditySources
                sources={liquiditySources}
                isLoading={isLoading}
                isWrap={isWrap}
                availableSourceCount={selectedLiquiditySourceCount}
                chainId={chainId}
              />
              <ExpandIcon className="text-token-gray-800 ml-1" />
            </div>
          </div>
        ) : liquiditySources ? (
          <>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Route
            </div>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              <LiquiditySources
                sources={liquiditySources}
                isLoading={isLoading}
                isWrap={isWrap}
                availableSourceCount={selectedLiquiditySourceCount}
                chainId={chainId}
              />
            </div>
          </>
        ) : isWrap ? (
          <>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Route
              <RouteDetailsTooltip
                availableSourceCount={selectedLiquiditySourceCount}
                chainId={chainId}
                isLoading={isLoading}
                tooltipTrigger={
                  <span>
                    <InfoCircleSolidSmallIcon className="text-token-gray-700 ml-1 flex justify-center cursor-default hover:cursor-default" />
                  </span>
                }
                liquiditySources={liquiditySources}
                isWrap={isWrap}
              />
            </div>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              <div className="flex items-center justify-center bg-primitive-basic-white rounded-xs border border-solid h-5 mr-1.5 w-5 text-token-gray-300">
                <ArrowDownIcon className="size-2.5 -rotate-90" />
              </div>
              Direct{' '}
              {sellToken.address === NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId] ? 'Wrap' : 'Unwrap'}
            </div>
          </>
        ) : (
          '-'
        )}
      </div>
      {!isWrap && walletAddress && (
        <ReceivingWalletLine
          chainId={chainId}
          walletAddress={walletAddress}
          connectedWallet={connectedWallet}
        />
      )}
    </>
  );
};

type ExpandedContentCrossChainModeProps = {
  isLoading: boolean;
  steps: CrossChainStep[];
  totalNetworkFee: string;
  estimatedTimeSeconds: number;
  nativeTokenUsd: number;
  chainType: 'evm' | 'svm';
  buyChainId?: number;
  slippageBps: number;
  buySideConnectedWalletAddress?: string;
  showReceivingWalletLine?: boolean;
};

export const ExpandedContentCrossChainMode = ({
  isLoading,
  steps,
  totalNetworkFee,
  estimatedTimeSeconds,
  nativeTokenUsd,
  chainType,
  buyChainId,
  slippageBps,
  buySideConnectedWalletAddress,
  showReceivingWalletLine,
}: ExpandedContentCrossChainModeProps) => {
  const slippageLabel = slippageBps / 100;

  return (
    <>
      <div className="flex items-center justify-between h-8 w-full">
        <div
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'flex items-center text-token-gray-800',
          })}
        >
          Network cost
          <MatchaTooltip delayDuration={0}>
            <MatchaTooltipTrigger>
              <span>
                <InfoCircleSolidSmallIcon className="text-token-gray-700 ml-1 flex justify-center cursor-default hover:cursor-default" />
              </span>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>
              Fee required in order to successfully conduct a transaction on the blockchain.
            </MatchaTooltipContent>
            <MatchaTooltipPopover className="max-w-[282px]" />
            <MatchaTooltipBottomSheet header="Network cost" />
          </MatchaTooltip>
        </div>
        {isLoading ? (
          <TextSkeleton width={150} height={14} />
        ) : (
          <div className="flex items-center">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              <PriceRecapCrossChainMode
                totalNetworkFee={totalNetworkFee}
                nativeTokenUsd={nativeTokenUsd}
                chainType={chainType}
              />
            </span>
          </div>
        )}
      </div>
      <MaxSlippageLineItemWithPopover
        isLoading={isLoading}
        slippagePercentageFormatted={slippageLabel}
      />
      <div className="flex items-center justify-between h-8 w-full">
        {isLoading ? (
          <>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Route
            </div>
            <TextSkeleton width={110} height={14} />
          </>
        ) : steps.length ? (
          <>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Route
            </div>
            <div className="flex items-center gap-1.5">
              {steps
                .filter((step) => step.type === 'bridge' && step.provider)
                .map((step, index) => (
                  <span key={`${step.provider}-${index}`} className="flex items-center gap-1">
                    {index > 0 && <span className="text-token-gray-700">{'\u2192'}</span>}
                    <BridgeAvatar name={step.provider ?? ''} size={16} />
                    <span className="text-token-gray-1600">
                      {getBridgeDisplayName(step.provider)}
                    </span>
                  </span>
                ))}
            </div>
          </>
        ) : (
          '-'
        )}
      </div>
      <div className="flex items-center justify-between h-8 w-full">
        {isLoading ? (
          <>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Estimated time
            </div>
            <TextSkeleton width={50} height={14} />
          </>
        ) : (
          <>
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex items-center text-token-gray-800',
              })}
            >
              Estimated time
            </div>
            <div className="flex items-center">
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                <span className="text-token-gray-1600">
                  ~ {Math.ceil(estimatedTimeSeconds / 60)} min
                </span>
              </div>
            </div>
          </>
        )}
      </div>
      {showReceivingWalletLine && buyChainId && (
        <ReceivingWalletLineCrossChainMode
          chainId={buyChainId}
          connectedWalletAddress={buySideConnectedWalletAddress}
        />
      )}
    </>
  );
};
